import { useState, useCallback, useEffect } from 'react';
import { showConfirm } from '@/utils/common/alert';
import { roleNamesAndDescriptions } from '@/types/admin/userManagement';
import { 
  validateData, 
  composeValidators, 
  requiredString, 
  emailFormat, 
  requireArrayLengthIf 
} from '@/utils/admin/validation';

export interface CreateUserFormData {
  email: string;
  firstName: string;
  lastName: string;
  roleName: string;
  organizations?: string[];
  properties?: string[];
}

export const useCreateUserForm = () => {
  const [formData, setFormData] = useState<CreateUserFormData>({
    email: '',
    firstName: '',
    lastName: '',
    roleName: '',
    organizations: [],
    properties: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeStep, setActiveStep] = useState(0);

  // Form field change handler
  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  // Step-specific validators
  const accountInfoValidator = composeValidators<CreateUserFormData>(
    requiredString('firstName', 'First name'),
    requiredString('lastName', 'Last name'),
    requiredString('email', 'Email'),
    emailFormat('email')
  );

  const roleSelectionValidator = composeValidators<CreateUserFormData>(
    requiredString('roleName', 'User role'),
    requireArrayLengthIf(
      (data) => data.roleName === roleNamesAndDescriptions.orgAdmin.value,
      'organizations',
      'organization'
    ),
    requireArrayLengthIf(
      (data) => [
        roleNamesAndDescriptions.propertyAdmin.value,
        roleNamesAndDescriptions.billingAdmin.value,
      ].includes(data.roleName),
      'properties',
      'property'
    )
  );

  // Form validation
  const validateCurrentStep = useCallback(() => {
    if (activeStep === 0) {
      return validateData(formData, setErrors, accountInfoValidator);
    } else if (activeStep === 1) {
      return validateData(formData, setErrors, roleSelectionValidator);
    }
    return true;
  }, [activeStep, formData]);

  // Step navigation
  const handleNext = useCallback(() => {
    if (validateCurrentStep()) {
      setActiveStep(prev => prev + 1);
    }
  }, [validateCurrentStep]);

  const handleBack = useCallback(() => {
    setActiveStep(prev => Math.max(0, prev - 1));
  }, []);

  // Jump to a specific step (used by desktop tabs)
  const goToStep = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(2, index));
    setActiveStep(clamped);
  }, []);

  // Complete form validator (combines all steps)
  const completeFormValidator = composeValidators<CreateUserFormData>(
    // Account info validation
    requiredString('firstName', 'First name'),
    requiredString('lastName', 'Last name'),
    requiredString('email', 'Email'),
    emailFormat('email'),
    // Role selection validation
    requiredString('roleName', 'User role'),
    requireArrayLengthIf(
      (data) => data.roleName === roleNamesAndDescriptions.orgAdmin.value,
      'organizations',
      'organization'
    ),
    requireArrayLengthIf(
      (data) => [
        roleNamesAndDescriptions.propertyAdmin.value,
        roleNamesAndDescriptions.billingAdmin.value,
      ].includes(data.roleName),
      'properties',
      'property'
    )
  );

  // Final form validation
  const validateForm = useCallback(() => {
    return validateData(formData, setErrors, completeFormValidator);
  }, [formData]);

  // Discard alert
  const showDiscardAlert = useCallback(async (onConfirm: () => void) => {
    if (formData.firstName || formData.lastName || formData.email || formData.roleName) {
      const confirmed = await showConfirm(
        'Discard Changes',
        'Are you sure you want to discard your changes?',
        'Discard',
        'Keep Editing'
      );
      
      if (confirmed) {
        onConfirm();
      }
    } else {
      onConfirm();
    }
  }, [formData]);

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        roleName: '',
        organizations: [],
        properties: [],
      });
      setErrors({});
      setActiveStep(0);
    };
  }, []);

  // Back to an empty form; the desktop side panel stays mounted across opens.
  const resetForm = useCallback(() => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      roleName: '',
      organizations: [],
      properties: [],
    });
    setErrors({});
    setActiveStep(0);
  }, []);

  return {
    formData,
    errors,
    activeStep,
    handleFieldChange,
    handleNext,
    handleBack,
    goToStep,
    validateCurrentStep,
    validateForm,
    showDiscardAlert,
    resetForm,
  };
};
